import React, { useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Form, Spinner } from 'react-bootstrap';
import { BsFillPlusCircleFill, BsXLg } from 'react-icons/bs';
import tinycolor from 'tinycolor2';
import { layoutDefaults } from '../../utils/layout-defaults';
import { ShouldFadeIn } from '../should-fade-in';

function UserAddressListItem({
  index,
  className,
  address,
  onSelect,
  onDelete,
  disabled = false,
}) {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const classes = useStyles({
    index,
    disabled: disabled && !loading,
    loading,
    nullAddress: address == null,
  });

  const radioRef = useRef();

  const baseLocationComponents = useMemo(() => {
    const separator = ', ';
    const location = address;

    let result =
      `${location?.neighborhood?.name ?? ''}${separator}${location?.city?.name ?? ''}${separator}${location?.state?.code ?? ''}`.replace(
        /(, )+/g,
        separator
      );

    if (result.startsWith(separator)) {
      result = result.substring(separator.length);
    }

    return result;
  }, [address]);

  const handleOnClick = () => {
    if (disabled) return;
    // setSelected(!selected);

    if (radioRef.current == null) {
      onSelect?.call(this, null, setLoading);
    } else {
      radioRef.current?.click();
    }
  };

  const handleOnChange = (event) => {
    if (disabled) return;

    event.stopPropagation();
    const { checked } = event.target;

    if (checked) {
      onSelect?.call(this, address, setLoading);
    }
  };

  const handleOnDelete = (event) => {
    if (disabled || loading) return;

    event.stopPropagation();
    onDelete?.call(this, address, index);
  };

  return (
    <ShouldFadeIn index={index}>
      <div
        className={classNames(classes.container, className)}
        onClick={handleOnClick}
        aria-hidden
      >
        {address != null && !loading && (
          <Form.Check
            ref={radioRef}
            type="radio"
            size="lg"
            name="place"
            disabled={disabled && !loading}
            className={classes.radio}
            readOnly={false}
            onChange={handleOnChange}
          />
        )}

        {address == null && !loading && (
          <div className={classes.icon}>
            <BsFillPlusCircleFill />
          </div>
        )}

        {loading && (
          <div>
            <Spinner className={classes.spinner} size="sm" />
          </div>
        )}

        {address != null && (
          <div className={classes.textContainer}>
            <div className={classes.routeContainer}>
              {!_.isEmpty(address.route) && <span>{address.route}, </span>}
              {_.isEmpty(address.route) && (
                <span className={classes.empty}>
                  {t('address-card.route.empty')},{' '}
                </span>
              )}
              {!_.isEmpty(address.number) && <span>{address.number}</span>}
              {_.isEmpty(address.number) && address.is_no_number !== true && (
                <span className={classes.empty}>
                  {t('address-card.street-number.empty')}
                </span>
              )}

              {_.isEmpty(address.number) && address.is_no_number === true && (
                <span>{t('address-card.no-street-number.term')}</span>
              )}

              {_.isEmpty(address.postal_code) && (
                <div className={classes.empty}>
                  {t('address-card.postal-code.empty')}
                </div>
              )}
            </div>

            <div className={classes.baseComponents}>
              {baseLocationComponents}
            </div>

            {!_.isEmpty(baseLocationComponents) && (
              <div className={classes.baseComponents}>
                {address.postal_code}
              </div>
            )}

            {!_.isEmpty(address.complement) && (
              <div className={classes.complement}>{address.complement}</div>
            )}
          </div>
        )}

        {address == null && (
          <div className={classes.textContainer}>
            {t('new-address-modal.title')}
          </div>
        )}

        {address != null && (
          <BsXLg className={classes.delete} onClick={handleOnDelete} />
        )}
      </div>
    </ShouldFadeIn>
  );
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',

    backgroundColor: layoutDefaults.cardColor,
    borderRadius: layoutDefaults.radius,

    paddingTop: ({ index }) =>
      index != null
        ? layoutDefaults.verticalMargin
        : layoutDefaults.verticalMargin - 2,
    paddingBottom: ({ index }) =>
      index != null
        ? layoutDefaults.verticalMargin
        : layoutDefaults.verticalMargin - 2,
    marginBottom: layoutDefaults.smallerVerticalMargin,

    paddingRight: layoutDefaults.horizontalMargin,

    // paddingTop: layoutDefaults.smallVerticalMargin,
    // paddingBottom: layoutDefaults.smallVerticalMargin,
    // paddingLeft: layoutDefaults.horizontalMargin,
    // paddingRight: layoutDefaults.horizontalMargin,

    // display: 'flex',
    // overflow: 'hidden',
    // background: layoutDefaults.cardColor,
    // borderRadius: layoutDefaults.radius,
    // cursor: 'pointer',
    // border: `1px solid red`,
  },
  radio: {
    marginLeft: layoutDefaults.smallestHorizontalMargin,
    paddingLeft: layoutDefaults.horizontalMargin,
    paddingRight: layoutDefaults.horizontalMargin,
  },
  icon: {
    fontSize: layoutDefaults.bigFontSize,
    marginLeft: layoutDefaults.smallestHorizontalMargin,
    paddingLeft: layoutDefaults.horizontalMargin + 1,
    paddingRight: layoutDefaults.horizontalMargin - 1,
    opacity: ({ disabled }) => (disabled ? layoutDefaults.opacity : 1),
  },
  delete: {
    opacity: ({ disabled, loading }) =>
      disabled || loading
        ? layoutDefaults.smallerOpacity
        : layoutDefaults.opacity,
    marginLeft: layoutDefaults.smallestHorizontalMargin,
    marginRight: layoutDefaults.smallestHorizontalMargin,
  },
  textContainer: {
    flex: 1,
    fontSize: layoutDefaults.fontSize,
    opacity: ({ disabled }) => (disabled ? layoutDefaults.opacity : 1),
    textAlign: 'left',
  },
  routeContainer: {
    textTransform: 'uppercase',
    fontSize: layoutDefaults.smallFontSize,
    // fontWeight: 'bold',
    // opacity: ({ selectable }) => (selectable ? 1 : layoutDefaults.opacity),
    // fontWeight: ({ showName }) => (showName ? 'normal' : 'bold'),
  },
  baseComponents: {
    opacity: layoutDefaults.opacity,
    fontSize: layoutDefaults.fontSize,
    // opacity: ({ selectable }) => (selectable ? 1 : layoutDefaults.opacity),
  },
  complement: {
    fontSize: layoutDefaults.fontSize,
    marginTop: layoutDefaults.smallerVerticalMargin,
    opacity: layoutDefaults.opacity - 0.2,
  },
  empty: {
    color: layoutDefaults.destructiveColor,
  },
  spinner: {
    fontSize: layoutDefaults.smallerFontSize,
    marginTop: ({ nullAddress }) =>
      nullAddress ? null : layoutDefaults.smallVerticalMargin,
    marginLeft:
      layoutDefaults.horizontalMargin + layoutDefaults.smallestHorizontalMargin,
    marginRight: layoutDefaults.horizontalMargin - 0.5,
    color: tinycolor(layoutDefaults.buttonBackgroundPrimary)
      .darken(20)
      .toHexString(),
  },
});

export { UserAddressListItem };
